import { motion } from "framer-motion"
import { Users, Heart, Zap, Target, Play } from "lucide-react"
import { useState, useCallback, useEffect, useRef } from "react";
import { HomepageLoader } from "../components/Homepage/HomepageLoader";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PricingPlanModal } from "../components/PricingPlanModal";
import { getCustomerPortalSession, getPlanList } from "../redux/actions";
import { DistributionModal } from "../components/DistributionAccess/DistributionModal";
import SeoHead from "../seo/SeoHead";
import { isPrerendering } from "../helper/isPrerendering";
import { generateBreadcrumbSchema, generateGraphSchema, generateWebPageSchema } from "../seo/schemaGenerator";
import { generateMetadata } from "../seo/metadataGenerator";
import { SITE_URL, PAGE_OG_IMAGES } from "../seo/constants";
import { usePrerenderReady } from "../hooks/usePrerenderReady";

export default function OurStoryPage() {
  usePrerenderReady();
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isLoad, setIsLoad] = useState(false)
  const [showDistributionModal, setShowDistributionModal] = useState(false)
  const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { hasActiveSubscription, subscriptionPlan } = useSelector((state) => state.billing);
  const planList = subscriptionPlan || [];
  const lastSubscriptionFetched = subscriptionPlan?.lastFetched;
  const token = localStorage.getItem("authToken");

  const updateCursorPosition = useCallback((e) => {
    requestAnimationFrame(() => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    });
  }, []);

  useEffect(() => {
    document.body.style.cursor = "none";
    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    const interactiveElements = document.querySelectorAll(
      'a, button, input, [role="button"]'
    );
    interactiveElements.forEach((element) => {
      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);
    });

    window.addEventListener("mousemove", updateCursorPosition);

    // Simulate loading time (remove this in production and use real loading logic)
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => {
      clearTimeout(timer);
      document.body.style.cursor = "auto";
      window.removeEventListener("mousemove", updateCursorPosition);
      interactiveElements.forEach((element) => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      });
    };
  }, [updateCursorPosition]);

  const hasFetched = useRef(false);
  useEffect(() => {
    const fetchPlans = async () => {
      const shouldFetch =
        !planList?.length ||
        !lastSubscriptionFetched ||
        Date.now() - lastSubscriptionFetched > 1000 * 60 * 30; // 30 mins cache

      if (shouldFetch && !hasFetched.current && !isPrerendering() && !!token) {
        hasFetched.current = true; // prevent duplicate fetches
        await dispatch(getPlanList("subscription"));
      }
    };

    fetchPlans();

    // reset flag when component unmounts or on logout, if needed
    return () => {
      hasFetched.current = false;
    };
  }, [dispatch, planList?.length, lastSubscriptionFetched]);

  const handleSubscription = async () => {
    if (token) {
      try {
        setIsLoad(true); // 🔹 start loading
        if (hasActiveSubscription) {
          await dispatch(getCustomerPortalSession());
        } else {
          setShowPricingPlanModal(true);
        }
      } catch (err) {
        console.error('Error handling Pro button:', err);
      } finally {
        setIsLoad(false); // 🔹 stop loading
      }
    } else {
      navigate('/signup')
    }
  }

  //SEO
  const metadata = generateMetadata({
    title:
      "Our Story | UpNComer",

    description:
      "Learn how UpNComer was created to empower independent artists with the tools, education and support needed to build sustainable music careers. Discover our mission, vision and commitment to helping musicians grow through AI-powered marketing, distribution, analytics, fan engagement and artist development solutions.",

    image: PAGE_OG_IMAGES.ourStory,

    path: "/our-story",
  })

  const webPageSchema = generateWebPageSchema({
    title:
      "Our Story | UpNComer",

    description:
      "Learn how UpNComer was created to empower independent artists with the tools, education and support needed to build sustainable music careers. Discover our mission, vision and commitment to helping musicians grow through AI-powered marketing, distribution, analytics, fan engagement and artist development solutions.",

    url: `${SITE_URL}/our-story`,
  })

  const breadcrumbItems = [
    {
      "name": "Home",
      "path": "/"
    },
    {
      "name": "Our Story",
      "path": "/our-story"
    }
  ]

  const schemas = generateGraphSchema([
    webPageSchema,
    generateBreadcrumbSchema(breadcrumbItems)
  ])

  return (
    <>
      {/* {isLoading && <HomepageLoader />} */}
      <SeoHead
        metadata={metadata}
        schemas={schemas}
      />
      <div
        className={`cursor-spotlight ${isHovering ? "hovering" : ""}`}
        style={{
          left: `${cursorPosition.x}px`,
          top: `${cursorPosition.y}px`,
          transform: `translate(-50%, -50%)`,
        }}
      >
        <div className="inner-circle"></div>
      </div>
      {/* <div
        className={`transition-all duration-1000 ${isLoading ? "opacity-0 blur-xl" : "opacity-100 blur-none"
          }`}
      > */}
      <div className="relative min-h-screen bg-gradient-to-br from-[#000456] via-[#00067E] via-[#0a1a8a] to-[#1e3a8a] overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] via-[#0a1a8a] via-[#1e3a8a] to-[#3b82f6] opacity-90" />
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
          />
        </div>
        {/* Enhanced Multi-layered Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] via-[#0a1a8a] via-[#1e3a8a] to-[#3b82f6] opacity-90" />

          {/* Floating orbs */}
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 10,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
            }}
            className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
          />

          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: 12,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
              delay: 3,
            }}
            className="absolute bottom-32 left-16 w-72 h-72 bg-gradient-to-br from-[#FA6138]/12 via-[#818cf8]/15 to-[#6366f1]/18 rounded-full blur-3xl"
          />
        </div>

        <main className="relative z-10 mt-16 md:mt-18 py-16 md:py-20">
          <div className="container max-w-4xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-20"
            >
              <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#c7d2fe] via-[#a5b4fc] to-[#818cf8] bg-clip-text text-transparent mb-6 drop-shadow-lg">
                Our Story
              </h1>
              <p className="bg-gradient-to-r from-[#818cf8]/80 via-[#6366f1]/85 to-[#4f46e5]/80 bg-clip-text text-transparent text-xl font-light max-w-3xl mx-auto leading-relaxed">
                DRIVEN BY ARTISTS, FOR ARTISTS
              </p>
            </motion.div>

            {/* Story Content */}
            <div className="space-y-16">
              {/* Mission */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#6366f1]/20 rounded-2xl p-8"
                style={{
                  boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                }}
              >
                <div className="flex items-start space-x-6">
                  <div className="p-4 bg-[#FA6138]/20 rounded-2xl">
                    <Heart className="w-8 h-8 text-[#FA6138]" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-4">
                      Born from Frustration
                    </h2>
                    <p className="text-[#c7d2fe] text-lg leading-relaxed mb-4">
                      We started UpNComer because we lived the struggle. As independent artists, we watched talented
                      musicians give up their dreams—not because they lacked talent, but because the industry was stacked
                      against them.
                    </p>
                    <p className="text-[#818cf8]/80 leading-relaxed">
                      Traditional music industry services cost thousands per month, require long-term contracts, and often
                      take a percentage of your royalties. We knew there had to be a better way.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Vision */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="bg-gradient-to-br from-[#1e1b4b]/20 via-[#312e81]/15 to-[#4338ca]/10 backdrop-blur-xl border border-[#4f46e5]/20 rounded-2xl p-8"
                style={{
                  boxShadow: "0 20px 40px rgba(79, 70, 229, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                }}
              >
                <div className="flex items-start space-x-6">
                  <div className="p-4 bg-[#FA6138]/20 rounded-2xl">
                    <Target className="w-8 h-8 text-[#FA6138]" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-4">
                      Our Mission
                    </h2>
                    <p className="text-[#c7d2fe] text-lg leading-relaxed mb-4">
                      To democratize the music industry by giving independent artists access to the same tools and
                      resources that major labels use—without the gatekeepers, without the contracts, and without giving
                      up control.
                    </p>
                    <p className="text-[#818cf8]/80 leading-relaxed">
                      Every artist deserves a fair shot at success. UpNComer levels the playing field by replacing
                      expensive industry services with AI-powered automation that works 24/7 to grow your career.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Technology */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="bg-gradient-to-br from-[#4338ca]/15 via-[#3730a3]/12 to-[#312e81]/18 backdrop-blur-xl border border-[#818cf8]/20 rounded-2xl p-8"
                style={{
                  boxShadow: "0 20px 40px rgba(129, 140, 248, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                }}
              >
                <div className="flex items-start space-x-6">
                  <div className="p-4 bg-[#FA6138]/20 rounded-2xl">
                    <Zap className="w-8 h-8 text-[#FA6138]" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-4">
                      The Power of AI
                    </h2>
                    <p className="text-[#c7d2fe] text-lg leading-relaxed mb-4">
                      We harness cutting-edge artificial intelligence to automate everything from marketing campaigns to
                      audio mastering. What used to require a team of expensive specialists can now be done by our AI—and
                      it never sleeps.
                    </p>
                    <p className="text-[#818cf8]/80 leading-relaxed">
                      Our Amplitude AI learns from millions of data points across the music industry, continuously
                      optimizing your campaigns and strategies to maximize your success.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Community */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#4338ca]/20 backdrop-blur-xl border border-[#a5b4fc]/20 rounded-2xl p-8"
                style={{
                  boxShadow: "0 20px 40px rgba(165, 180, 252, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                }}
              >
                <div className="flex items-start space-x-6">
                  <div className="p-4 bg-[#FA6138]/20 rounded-2xl">
                    <Users className="w-8 h-8 text-[#FA6138]" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-4">
                      Building Community
                    </h2>
                    <p className="text-[#c7d2fe] text-lg leading-relaxed mb-4">
                      UpNComer isn't just a platform—it's a movement. We're building a community of independent artists
                      who support each other, share knowledge, and celebrate each other's successes.
                    </p>
                    <p className="text-[#818cf8]/80 leading-relaxed">
                      Together, we're proving that independent artists don't need major labels to achieve their dreams.
                      The future of music is independent, and it starts with UpNComer.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="text-center"
            >
              <h2 className="text-3xl font-bold text-[#c7d2fe] mt-8 mb-6">Ready to Build Your Music Empire?</h2>
              <p className="text-[#818cf8]/80 mb-8 max-w-2xl mx-auto">
                Join hundreds of independent artists who are already growing their careers with UpNComer.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleSubscription}
                  className="inline-block bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white text-center
                         px-8 py-4 text-sm font-medium tracking-wider rounded-xl hover:from-[#e55532] hover:to-[#FA6138]
                         transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
                >
                  BECOME AN UPNCOMER
                </button>
                <button
                  onClick={() => setShowDistributionModal(true)}
                  className="inline-block bg-white text-theme_clr2 text-center
                             px-5 py-4 text-sm font-medium tracking-wider rounded-xl hover:from-[#e55532] hover:to-[#FA6138]
                             transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105
                             backdrop-blur-sm border border-white/10"
                  style={{
                    boxShadow: "0 10px 30px rgba(250, 97, 56, 0.3)",
                  }}
                >
                  DISTRIBUTE YOUR MUSIC
                </button>
                <a
                  href="https://www.instagram.com/upncomer.co/"
                  className="inline-flex items-center justify-center bg-[#1e3a8a]/20 border border-[#3b82f6]/30 text-[#93c5fd] 
                         px-8 py-4 text-sm font-medium tracking-wider rounded-xl hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40
                         transition-all duration-300 backdrop-blur-xl shadow-lg hover:shadow-xl"
                >
                  {/* <Play className="w-4 h-4 mr-2" /> */}
                  <span>FOLLOW THE BUILD <i class="ml-2 bi bi-wrench"></i></span>
                </a>
              </div>
            </motion.div>
            {/* Additional gradient overlay for smoother transition */}
            <div
              className="absolute left-0 right-0 bottom-0 h-32 z-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to bottom, transparent, rgba(0, 6, 126, 0.3) 50%, rgba(0, 4, 86, 0.6) 100%)",
              }}
            ></div>
            {/* Stats */}
            {/* <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1 }}
                className="mt-20"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {[
                    {
                      value: "450+",
                      label: "Artists Empowered",
                      bgColor: "bg-[#1e3a8a]/15",
                      borderColor: "border-[#3b82f6]/20",
                    },
                    {
                      value: "2.3M+",
                      label: "Streams Generated",
                      bgColor: "bg-[#0a1a8a]/15",
                      borderColor: "border-[#1e3a8a]/25",
                    },
                    {
                      value: "$50K+",
                      label: "Artist Revenue Protected",
                      bgColor: "bg-[#3b82f6]/15",
                      borderColor: "border-[#60a5fa]/20",
                    },
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, delay: 1.2 + index * 0.1 }}
                      className={`${stat.bgColor} backdrop-blur-xl border ${stat.borderColor} rounded-2xl p-8 text-center hover:bg-[#3b82f6]/10 transition-all duration-300`}
                      style={{
                        boxShadow: "0 10px 30px rgba(30, 58, 138, 0.2), inset 0 1px 0 rgba(59, 130, 246, 0.1)",
                      }}
                    >
                      <div className="text-4xl font-bold text-[#FA6138] mb-2 drop-shadow-lg">{stat.value}</div>
                      <div className="text-[#93c5fd] font-light">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>
              </motion.div> */}
          </div>
        </main>
      </div>
      {/* </div> */}
      {/* Distribution Modal */}
      <DistributionModal
        isOpen={showDistributionModal}
        onClose={() => setShowDistributionModal(false)}
      />
      <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/dashboard"} />
    </>
  )
}
